import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import { type SprintView } from '@apm/contracts';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { cn } from '../../../lib/utils';
import { sprintColor } from '../../sprints/color';

/**
 * Giá trị "xem cả dự án", đi thẳng lên URL — APM-GANTT-q.
 *
 * Phải khác `undefined`: URL không có `sprintId` nghĩa là **chưa chọn**, và
 * server tự lấy sprint đang chạy. Nếu "cả dự án" cũng là `undefined` thì mỗi
 * lần tải lại trang nó tự nhảy về sprint — người dùng không giữ được lựa chọn
 * của mình.
 */
export const ALL_SPRINTS = 'all';

/** Chấm màu nhận dạng — cùng màu với viền khung sprint ở Board. */
function Dot({ sprint }: { readonly sprint: SprintView }): ReactNode {
  return (
    <span
      aria-hidden
      className="size-2.5 shrink-0 rounded-full"
      style={{ backgroundColor: sprintColor(sprint) }}
    />
  );
}

/**
 * Bộ chọn sprint dùng chung cho cả ba cách nhìn — APM-BOARD-b.
 *
 * ## Vì sao một component, đặt được ở hai chỗ
 *
 * Ở cách nhìn **Board** nó nằm trên đầu khung sprint, vì khung là thứ nó điều
 * khiển. Ở Gantt và Danh sách không có khung nào, nên nó nằm trên thanh công
 * cụ. Hai chỗ đặt, **một** trạng thái — và trạng thái ấy sống ở URL, nên đổi
 * cách nhìn không làm mất sprint đang xem.
 *
 * Hai bộ chọn riêng cho hai chỗ là cách chắc chắn để chúng lệch nhau: người
 * dùng chọn Sprint 8 ở Board, sang Gantt thấy Sprint 7, và không có gì nói cho
 * họ biết vì sao.
 *
 * ## ⚠️ Vì sao không phải `<select>`
 *
 * Màu nhận dạng phải hiện **trong danh sách**, không chỉ sau khi đã chọn — chọn
 * xong mới biết màu thì màu không giúp gì cho việc chọn. Mà `<option>` thì
 * không chứa được phần tử con: chấm màu phải là một `<span>`, và nền màu đặt
 * thẳng lên `<option>` chỉ ăn ở một phần trình duyệt. Một kênh nhận dạng chỉ
 * hiện ở nửa số máy còn tệ hơn không có kênh nào.
 *
 * Màu vẫn là kênh **thứ hai**: tên sprint nằm ngay cạnh chấm, và dấu tích chỉ
 * ra mục đang chọn (docs/frontend-guide.md §4).
 */
export function SprintPicker({
  sprints,
  value,
  onChange,
  allowAll = false,
  id = 'sprint-picker',
  disabled = false,
}: {
  readonly sprints: readonly SprintView[];
  /** `null` = xem tất cả, không theo sprint nào. */
  readonly value: string | null;
  readonly onChange: (sprintId: string | null) => void;
  /**
   * Cho phép mục "Cả dự án" — chỉ Gantt và Danh sách, **không** Board.
   *
   * Xem chú thích ở `DropdownMenuContent`: board không theo sprint nào là board
   * gộp cột của mọi sprint, còn Gantt và Danh sách không có cột nên "cả dự án"
   * ở đó là cách nhìn bình thường nhất — thậm chí là cách nhìn kinh điển của
   * một biểu đồ Gantt.
   */
  readonly allowAll?: boolean;
  readonly id?: string;
  /** Khoá chứ không ẩn — xem `PeriodPicker`. */
  readonly disabled?: boolean;
}): ReactNode {
  const { t } = useTranslation();

  // Dự án Waterfall không có sprint nào — một ô chọn luôn rỗng là một câu hỏi
  // không trả lời được.
  if (sprints.length === 0) return null;

  const selected = sprints.find((sprint) => sprint.id === value) ?? null;
  const ordered = [...sprints].sort((a, b) => b.sequence - a.sequence);

  return (
    <span
      className={cn(
        'flex items-center gap-1.5 text-xs text-muted-foreground',
        disabled && 'opacity-50',
      )}
    >
      {/* Nhãn **không** gắn `htmlFor`: nhãn liên kết sẽ thay tên của nút, và
          trình đọc màn hình chỉ nghe "Sprint:" mà không nghe sprint nào đang
          chọn. Để rời ra thì tên nút chính là nội dung nó đang hiện. */}
      <span aria-hidden>{t('board.sprintPicker')}</span>

      <DropdownMenu>
        <DropdownMenuTrigger
          id={id}
          disabled={disabled}
          className="flex h-7 max-w-52 items-center gap-1.5 rounded-control border border-border-strong bg-background px-1.5 text-sm text-foreground"
        >
          {selected ? <Dot sprint={selected} /> : null}
          <span className="truncate">
            {selected
              ? selected.name
              : value === ALL_SPRINTS
                ? t('board.sprintPickerAll')
                : t('board.sprintPickerNone')}
          </span>
          <ChevronDown aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
        </DropdownMenuTrigger>

        {/*
          ⚠️ "Cả dự án" chỉ có ở Gantt và Danh sách — APM-BOARD-b, APM-GANTT-q.

          Ở **Board** thì không: từ khi cột thuộc về sprint, một board không theo
          sprint nào là board gộp cột của **mọi** sprint — ba cột cùng tên "Hoàn
          thành" đứng cạnh nhau, và không gì nói cột nào của hai tuần nào.

          Gantt và Danh sách không có cột, nên câu hỏi ấy không đặt ra: xem cả dự
          án ở đó là cách nhìn kinh điển của một Gantt. Trước đây chúng **tự**
          rơi vào cách nhìn ấy sau vài thao tác mà không ai chọn (xem `absorb`),
          nghĩa là ứng dụng vẫn làm điều đó — chỉ là làm sau lưng người dùng.
          Nay là một mục bấm được, và chỉ đổi khi người ta bấm.
        */}
        <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
          {allowAll ? (
            <DropdownMenuItem onSelect={() => onChange(ALL_SPRINTS)} className="gap-1.5">
              {/* Ô trống giữ chỗ của chấm màu, để chữ của mọi mục thẳng hàng. */}
              <span aria-hidden className="size-2.5 shrink-0" />
              <span className="truncate">{t('board.sprintPickerAll')}</span>
              {value === ALL_SPRINTS ? (
                <Check aria-hidden className="ml-auto size-3.5 text-foreground" />
              ) : null}
            </DropdownMenuItem>
          ) : null}

          {ordered.map((sprint) => (
            <DropdownMenuItem
              key={sprint.id}
              onSelect={() => onChange(sprint.id)}
              className="gap-1.5"
            >
              <Dot sprint={sprint} />
              <span className="truncate">{sprint.name}</span>
              {sprint.status === 'active' ? (
                <span className="text-xs text-muted-foreground">· {t('board.sprintRunning')}</span>
              ) : null}
              {sprint.id === value ? (
                <Check aria-hidden className="ml-auto size-3.5 text-foreground" />
              ) : null}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}
